import { useState, useEffect, useRef } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useSendEmailCode, useVerifyEmailCode } from '@/hooks/useEmailVerification';
import config from '@/config';

interface EmailVerificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerificationSuccess?: () => void;
}

type Step = 'input' | 'verify' | 'success';

export function EmailVerificationDialog({
  open,
  onOpenChange,
  onVerificationSuccess,
}: EmailVerificationDialogProps) {
  const [step, setStep] = useState<Step>('input');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileStatus, setTurnstileStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [countdown, setCountdown] = useState(0);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const sendCodeMutation = useSendEmailCode();
  const verifyCodeMutation = useVerifyEmailCode();

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setStep('input');
      setEmail('');
      setCode('');
      setTurnstileToken('');
      setTurnstileStatus('loading');
      setCountdown(0);
    }
  }, [open]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendCode = async () => {
    if (!email || !turnstileToken) {
      return;
    }

    const result = await sendCodeMutation.mutateAsync({
      email: email.toLowerCase().trim(),
      turnstile: turnstileToken,
    });

    if (result.code === 0) {
      setStep('verify');
      setCountdown(60); // 60 second cooldown before resend
    }
  };

  const handleVerifyCode = async () => {
    if (!email || !code) {
      return;
    }

    const result = await verifyCodeMutation.mutateAsync({
      email: email.toLowerCase().trim(),
      code: code.trim(),
    });

    if (result.code === 0) {
      setStep('success');
      // Call success callback if provided
      onVerificationSuccess?.();
      setTimeout(() => {
        onOpenChange(false);
      }, 2000);
    }
  };

  const handleResendCode = () => {
    setTurnstileToken('');
    setTurnstileStatus('loading');
    setStep('input');
  };

  const handleRetryTurnstile = () => {
    setTurnstileStatus('loading');
    setTurnstileToken('');
    turnstileRef.current?.reset();
  };

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSend = isEmailValid && turnstileToken && !sendCodeMutation.isPending;
  const canVerify = code.length === 6 && !verifyCodeMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px]"
        style={{
          backgroundColor: '#1A1A1A',
          border: '3px solid #50E5F8',
          boxShadow: '0 0 40px rgba(80, 229, 248, 0.4), 0 0 80px rgba(80, 229, 248, 0.2)',
          borderRadius: '16px',
        }}
      >
        <DialogHeader className="pt-4">
          <DialogTitle
            className="text-3xl text-center"
            style={{
              fontFamily: 'Hyperspace Race Capsule, cursive',
              color: '#50E5F8',
              textShadow: '0 0 20px rgba(80, 229, 248, 0.5)',
            }}
          >
            {step === 'input' && 'VERIFY YOUR EMAIL'}
            {step === 'verify' && 'ENTER VERIFICATION CODE'}
            {step === 'success' && 'EMAIL VERIFIED!'}
          </DialogTitle>
          <DialogDescription
            className="text-base text-center pt-2"
            style={{
              fontFamily: 'Outfit, sans-serif',
              color: 'rgba(255, 255, 255, 0.8)',
            }}
          >
            {step === 'input' &&
              'Enter your email address to receive a verification code'}
            {step === 'verify' &&
              'Check your email and enter the 6-digit code we sent you'}
            {step === 'success' && 'Your email has been successfully verified!'}
          </DialogDescription>
        </DialogHeader>

        {step === 'input' && (
          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <label
                htmlFor="email"
                className="text-base font-semibold block"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  color: '#50E5F8',
                }}
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={sendCodeMutation.isPending}
                className={cn(
                  'w-full p-4 rounded-lg text-lg',
                  'focus:outline-none',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  backgroundColor: '#262626',
                  border: '2px solid #50E5F8',
                  color: 'white',
                  boxShadow: '0 0 10px rgba(80, 229, 248, 0.2)',
                }}
              />
            </div>

            <div className="flex flex-col items-center gap-2 py-2">
              {turnstileStatus === 'loading' && (
                <div className="flex items-center gap-2 text-sm text-white/60">
                  <Loader2 className="animate-spin" size={16} />
                  <span style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Loading verification...
                  </span>
                </div>
              )}

              {turnstileStatus === 'error' && (
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-2 text-sm text-red-400">
                    <AlertCircle size={16} />
                    <span style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Verification failed to load
                    </span>
                  </div>
                  <button
                    onClick={handleRetryTurnstile}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:bg-white/10"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      border: '1px solid rgba(80, 229, 248, 0.5)',
                      color: '#50E5F8',
                    }}
                  >
                    <RefreshCw size={14} />
                    Retry
                  </button>
                </div>
              )}

              {turnstileStatus === 'ready' && turnstileToken && (
                <div className="flex items-center gap-2 text-sm text-green-400">
                  <span style={{ fontFamily: 'Outfit, sans-serif' }}>
                    ✓ Verification complete
                  </span>
                </div>
              )}

              <Turnstile
                ref={turnstileRef}
                siteKey={config.CLOUDFLARE_SITEKEY}
                onSuccess={(token) => {
                  setTurnstileToken(token);
                  setTurnstileStatus('ready');
                }}
                onError={() => {
                  setTurnstileToken('');
                  setTurnstileStatus('error');
                }}
                onExpire={() => {
                  setTurnstileToken('');
                  setTurnstileStatus('loading');
                }}
                onLoad={() => {
                  // Widget has loaded, waiting for user interaction or auto-pass
                }}
              />
            </div>

            <DialogFooter className="flex-col gap-2">
              <button
                onClick={handleSendCode}
                disabled={!canSend}
                className={cn(
                  'w-full py-4 rounded-lg text-lg font-bold transition-all',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  !canSend ? '' : 'hover:scale-105'
                )}
                style={{
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  background: canSend
                    ? 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)'
                    : '#555555',
                  color: '#000000',
                  boxShadow: canSend ? '0 4px 20px rgba(251, 139, 0, 0.5)' : 'none',
                }}
              >
                {sendCodeMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin" size={20} />
                    SENDING...
                  </span>
                ) : (
                  'SEND VERIFICATION CODE'
                )}
              </button>

              {/* Help text for disabled state */}
              {!canSend && !sendCodeMutation.isPending && (
                <p
                  className="text-xs text-center"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    color: 'rgba(255, 255, 255, 0.5)',
                  }}
                >
                  {!isEmailValid && email.length > 0
                    ? 'Please enter a valid email address'
                    : !turnstileToken
                      ? 'Complete the verification above to continue'
                      : ''}
                </p>
              )}
            </DialogFooter>
          </div>
        )}

        {step === 'verify' && (
          <div className="space-y-6 py-4">
            <div className="space-y-4">
              <label
                htmlFor="code"
                className="text-base font-semibold block text-center"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  color: '#50E5F8',
                }}
              >
                Verification Code
              </label>
              <input
                id="code"
                type="text"
                placeholder="000000"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                disabled={verifyCodeMutation.isPending}
                className={cn(
                  'w-full p-6 text-center text-4xl tracking-[0.5em] font-mono',
                  'rounded-lg',
                  'focus:outline-none',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                style={{
                  backgroundColor: '#262626',
                  border: '3px solid #50E5F8',
                  color: '#50E5F8',
                  boxShadow: '0 0 20px rgba(80, 229, 248, 0.3)',
                  fontWeight: 'bold',
                }}
              />
              <p
                className="text-sm text-center"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  color: 'rgba(255, 255, 255, 0.6)',
                }}
              >
                Code expires in 10 minutes
              </p>
            </div>

            <DialogFooter className="flex-col gap-3">
              <button
                onClick={handleVerifyCode}
                disabled={!canVerify}
                className={cn(
                  'w-full py-4 rounded-lg text-lg font-bold transition-all',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  !canVerify ? '' : 'hover:scale-105'
                )}
                style={{
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  background: canVerify
                    ? 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)'
                    : '#555555',
                  color: '#000000',
                  boxShadow: canVerify ? '0 4px 20px rgba(251, 139, 0, 0.5)' : 'none',
                }}
              >
                {verifyCodeMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin" size={20} />
                    VERIFYING...
                  </span>
                ) : (
                  'VERIFY EMAIL'
                )}
              </button>

              {countdown > 0 ? (
                <p
                  className="text-sm text-center pt-2"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    color: 'rgba(255, 255, 255, 0.6)',
                  }}
                >
                  Resend code in {countdown}s
                </p>
              ) : (
                <button
                  onClick={handleResendCode}
                  disabled={sendCodeMutation.isPending}
                  className="w-full py-3 rounded-lg text-base font-semibold transition-all hover:bg-white/10"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    backgroundColor: 'transparent',
                    border: '2px solid rgba(80, 229, 248, 0.5)',
                    color: '#50E5F8',
                  }}
                >
                  RESEND CODE
                </button>
              )}
            </DialogFooter>
          </div>
        )}

        {step === 'success' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-6">
            {/* Success Icon */}
            <div
              className="w-28 h-28 rounded-full flex items-center justify-center animate-pulse"
              style={{
                background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                boxShadow: '0 0 40px rgba(76, 175, 80, 0.6), 0 0 80px rgba(76, 175, 80, 0.3)',
              }}
            >
              <svg
                className="w-16 h-16"
                fill="none"
                stroke="white"
                viewBox="0 0 24 24"
                strokeWidth={3}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            {/* Success Message */}
            <p
              className="text-xl text-center font-semibold"
              style={{
                fontFamily: 'Outfit, sans-serif',
                color: '#50E5F8',
              }}
            >
              Email verified successfully!
            </p>

            <p
              className="text-sm text-center"
              style={{
                fontFamily: 'Outfit, sans-serif',
                color: 'rgba(255, 255, 255, 0.5)',
              }}
            >
              This dialog will close automatically
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
